import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult, DroppableProvided, DraggableProvided } from '@hello-pangea/dnd';
import { PlusIcon, TrashIcon, UserIcon, UserGroupIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Input } from '../Shared/Input';
import { Button } from '../Shared/Button';
import { TextArea } from '../Shared/TextArea';
import { Workflow, WorkflowStep, WorkflowStepAssignee } from '../../services/workflowService';
import { workflowService } from '../../services/workflowService';
import { validateWorkflowDefinition } from '../../utils/workflowDefinitionValidation';
import {
  isArchiveStepType,
  isPublishStepType,
  isSignatureStepType,
  WorkflowActionCodes,
  WorkflowStepType,
} from '../../utils/workflowConstants';
import {
  buildAllowedActionsForStep,
  canDeleteWorkflowStep,
  canMoveStepToIndex,
  commentApplicableForStepType,
  getAutomaticActionsForStep,
  getDefaultWorkflowSteps,
  getStepTypeChangeBlockReason,
  insertNewEditStep,
  placeArchiveAsLastStep,
  placePublishBeforeArchive,
  placeSignatureAdjacentToPublish,
  sortStepsByOrder,
  stepAllowsComment,
  stepCanMoveSomewhere,
  syncAllStepsAllowedActionsFromPolicy,
  syncStepAllowedActionsFromPolicy,
} from '../../utils/workflowStepConstraints';
import { useToast } from '../UI/ToastProvider';
import { slaService, SlaDto } from '../../services/slaService';
import { userService } from '../../services/userService';
import { teamService } from '../../services/teamService';
import { User } from '../../types/users';
import { Team } from '../../types/teams';

const getCustomerId = (): string =>
  sessionStorage.getItem('@KnowInDoc:customer_id')?.trim() ?? '';

const ACTION_LABELS: Readonly<Record<string, string>> = {
  Save: 'Salvar',
  SendForReview: 'Enviar para revisão',
  Approve: 'Aprovar',
  Reject: 'Rejeitar',
  Publish: 'Publicar',
  Archive: 'Arquivar',  
  SendForSignature: 'Enviar para assinatura',
  CompleteEdit: 'Avançar entre edições',
  Comment: 'Comentar',
  Sign: 'Assinar',
  Edit: 'Editar / Reativar (nova versão)',
};

function actionLabel(code: string): string {
  return ACTION_LABELS[code] ?? code;
}

function displayActionsForStep(
  steps: WorkflowStep[],
  step: Pick<WorkflowStep, 'id' | 'allowedActions'>
): string[] {
  const automatic = getAutomaticActionsForStep(steps, step.id);
  const withComment = stepAllowsComment(step) ? [...automatic, WorkflowActionCodes.Comment] : automatic;
  return withComment.filter(a => a.toLowerCase() !== 'delete');
}

const STEP_TYPES = [
  { value: 'Edit', label: 'Edição' },
  { value: 'Review', label: 'Revisão' },
  { value: 'Signature', label: 'Assinatura' },
  { value: 'Publish', label: 'Publicação' },
  { value: 'Archive', label: 'Arquivamento' },
];

interface LocalAssignee {
  id?: string;
  userId?: string;
  teamId?: string;
  displayName: string;
  type: 'user' | 'team';
}

interface LocalStep extends Omit<WorkflowStep, 'assignees'> {
  localAssignees: LocalAssignee[];
}

function toLocalStep(step: WorkflowStep | LocalStep): LocalStep {
  const maybeLocal = step as LocalStep;
  if (Array.isArray(maybeLocal.localAssignees)) {
    const { assignees: _omit, ...rest } = step as WorkflowStep & LocalStep;
    return { ...(rest as unknown as LocalStep), localAssignees: maybeLocal.localAssignees };
  }
  const w = step as WorkflowStep;
  return {
    ...w,
    localAssignees: (w.assignees ?? []).map(a => ({
      id: a.id,
      userId: a.userId,
      teamId: a.teamId,
      displayName: a.userId ? `user:${a.userId}` : `team:${a.teamId}`,
      type: a.userId ? 'user' : 'team',
    })),
  };
}

function localStepsToWorkflowSteps(lst: LocalStep[]): WorkflowStep[] {
  return lst.map(({ localAssignees, ...rest }) => ({
    ...rest,
    assignees: [],
  }));
}

interface WorkflowConfigProps {
  workflow: Workflow | null;
  onSave: (workflow: Omit<Workflow, 'id'>) => void;
  onCancel: () => void;
  /** Somente leitura (ex.: workflow de sistema) — sem salvar, reordenar ou editar etapas */
  readOnly?: boolean;
}

function slaLabel(slas: SlaDto[], slaId?: string): string {
  if (!slaId) return '— Sem SLA —';
  return slas.find(s => s.id === slaId)?.name ?? slaId;
}

export const WorkflowConfig: React.FC<WorkflowConfigProps> = ({ workflow, onSave, onCancel, readOnly = false }) => {
  const { addToast } = useToast();
  const [name, setName] = useState(workflow?.name ?? '');
  const [description, setDescription] = useState(workflow?.description ?? '');
  const [steps, setSteps] = useState<LocalStep[]>([]);
  const [definitionErrors, setDefinitionErrors] = useState<string[]>([]);

  const [slas, setSlas] = useState<SlaDto[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);

  const [addAssigneeStep, setAddAssigneeStep] = useState<string | null>(null);
  const [addAssigneeType, setAddAssigneeType] = useState<'user' | 'team'>('user');
  const [addAssigneeId, setAddAssigneeId] = useState('');

  const orderedSteps = useMemo(() => sortStepsByOrder(steps), [steps]);
  const stepsForValidation = useMemo(() => localStepsToWorkflowSteps(orderedSteps), [orderedSteps]);

  const workflowIdentity = workflow?.id ?? '__new__';

  useEffect(() => {
    if (workflow) {
      setName(workflow.name);
      setDescription(workflow.description);
      const ordered = sortStepsByOrder(workflow.steps ?? []).map((s, i) => ({
        ...s,
        order: i + 1,
      }));
      setSteps(syncAllStepsAllowedActionsFromPolicy(ordered).map(toLocalStep));
    } else {
      setName('');
      setDescription('');
      setSteps(syncAllStepsAllowedActionsFromPolicy(getDefaultWorkflowSteps()).map(toLocalStep));
    }
    setDefinitionErrors([]);
  }, [workflowIdentity]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAuxData = useCallback(async () => {
    const customerId = getCustomerId();
    try {
      const [slasData, usersData, teamsData] = await Promise.all([
        customerId ? slaService.getSlas(customerId) : Promise.resolve([]),
        userService.getUsers('', 1, 100).then(r => r.users),
        teamService.getTeams(),
      ]);
      setSlas(slasData);
      setUsers(usersData);
      setTeams(teamsData);

      if (workflow?.steps?.length) {
        setSteps(prev =>
          prev.map(step => {
            const enriched = step.localAssignees.map(a => {
              if (a.userId) {
                const u = usersData.find(u => u.id === a.userId);
                return { ...a, displayName: u ? u.name : a.displayName };
              }
              if (a.teamId) {
                const t = teamsData.find(t => t.id === a.teamId);
                return { ...a, displayName: t ? t.name : a.displayName };
              }
              return a;
            });
            return { ...step, localAssignees: enriched };
          })
        );
      }
    } catch {
      // silently ignore
    }
  }, [workflow]);

  useEffect(() => {
    loadAuxData();
  }, [loadAuxData]);

  const handleAddStep = () => {
    setSteps(prev =>
      syncAllStepsAllowedActionsFromPolicy(
        insertNewEditStep(prev as unknown as WorkflowStep[])
      ).map(toLocalStep)
    );
  };

  const handleRemoveStep = (stepId: string) => {
    if (!canDeleteWorkflowStep(stepsForValidation, stepId)) {
      addToast('Não é possível remover esta etapa sem violar as regras do fluxo.', 'error');
      return;
    }
    setSteps(prev =>
      syncAllStepsAllowedActionsFromPolicy(
        prev
          .filter(s => s.id !== stepId)
          .map((s, i) => ({ ...s, order: i + 1 })) as unknown as WorkflowStep[]
      ).map(toLocalStep)
    );
  };

  const handleDragEnd = (result: DropResult) => {
    if (readOnly) return;
    if (!result.destination) return;
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    if (!canMoveStepToIndex(stepsForValidation, sourceIndex, destIndex)) {
      addToast('Essa posição não é permitida para as regras do fluxo.', 'error');
      return;
    }
    const items = [...orderedSteps];
    const [moved] = items.splice(sourceIndex, 1);
    items.splice(destIndex, 0, moved);
    const reordered = items.map((s, i) => ({ ...s, order: i + 1 }));
    setSteps(syncAllStepsAllowedActionsFromPolicy(reordered as unknown as WorkflowStep[]).map(toLocalStep));

    if (workflow?.id) {
      const serverSteps = workflow.steps ?? [];
      const serverIds = new Set(serverSteps.map(s => s.id));
      const reorderedIds = reordered.map(s => s.id);
      const canPersistOrder =
        serverSteps.length > 0 &&
        reordered.length === serverSteps.length &&
        reordered.every(s => serverIds.has(s.id)) &&
        new Set(reorderedIds).size === reorderedIds.length;

      if (canPersistOrder) {
        workflowService
          .updateWorkflowStepsOrder(
            workflow.id,
            reordered.map((s: LocalStep, i) => ({ id: s.id, order: i + 1 }))
          )
          .catch(() => {});
      }
    }
  };

  const updateStep = (stepId: string, patch: Partial<LocalStep>) => {
    setSteps(prev => prev.map(s => (s.id === stepId ? { ...s, ...patch } : s)));
  };

  const handleStepTypeChange = (stepId: string, newType: string) => {
    const ordered = orderedSteps;

    const typeChangeBlock = getStepTypeChangeBlockReason(stepsForValidation, stepId, newType);
    if (typeChangeBlock) {
      addToast(typeChangeBlock, 'error');
      return;
    }

    if (isSignatureStepType(newType)) {
      if (!ordered.some(s => isPublishStepType(s.type))) {
        addToast('É necessária uma etapa do tipo Publicação para usar Assinatura.', 'error');
        return;
      }
      if (ordered.some(s => s.id !== stepId && isSignatureStepType(s.type))) {
        addToast('O fluxo permite no máximo uma etapa de Assinatura.', 'error');
        return;
      }
      setSteps(prev =>
        syncAllStepsAllowedActionsFromPolicy(
          placeSignatureAdjacentToPublish(prev as unknown as WorkflowStep[], stepId)
        ).map(toLocalStep)
      );
      return;
    }

    if (isArchiveStepType(newType)) {
      if (ordered.some(s => s.id !== stepId && isArchiveStepType(s.type))) {
        addToast('O fluxo permite no máximo uma etapa de Arquivamento.', 'error');
        return;
      }
      setSteps(prev =>
        syncAllStepsAllowedActionsFromPolicy(
          placeArchiveAsLastStep(prev as unknown as WorkflowStep[], stepId)
        ).map(toLocalStep)
      );
      return;
    }

    if (isPublishStepType(newType)) {
      if (ordered.some(s => s.id !== stepId && isPublishStepType(s.type))) {
        addToast('O fluxo permite no máximo uma etapa de Publicação.', 'error');
        return;
      }
      if (ordered.some(s => isArchiveStepType(s.type))) {
        setSteps(prev =>
          syncAllStepsAllowedActionsFromPolicy(
            placePublishBeforeArchive(prev as unknown as WorkflowStep[], stepId)
          ).map(toLocalStep)
        );
        return;
      }
      const cur = ordered.find(s => s.id === stepId);
      const preserveComment = cur ? stepAllowsComment(cur) : false;
      setSteps(prev =>
        syncAllStepsAllowedActionsFromPolicy(
          syncStepAllowedActionsFromPolicy(
            prev.map(s => (s.id === stepId ? { ...s, type: newType } : s)) as unknown as WorkflowStep[],
            stepId,
            preserveComment && commentApplicableForStepType(newType)
          )
        ).map(toLocalStep)
      );
      return;
    }

    if (newType === WorkflowStepType.Edit || newType === 'Edit') {
      const cur = ordered.find(s => s.id === stepId);
      const preserveComment = cur ? stepAllowsComment(cur) : false;
      setSteps(prev =>
        syncAllStepsAllowedActionsFromPolicy(
          syncStepAllowedActionsFromPolicy(
            prev.map(s => (s.id === stepId ? { ...s, type: newType } : s)) as unknown as WorkflowStep[],
            stepId,
            preserveComment && commentApplicableForStepType(newType)
          )
        ).map(toLocalStep)
      );
      return;
    }

    const cur = ordered.find(s => s.id === stepId);
    const preserveComment = cur ? stepAllowsComment(cur) : false;
    setSteps(prev =>
      syncAllStepsAllowedActionsFromPolicy(
        syncStepAllowedActionsFromPolicy(
          prev.map(s => (s.id === stepId ? { ...s, type: newType } : s)) as unknown as WorkflowStep[],
          stepId,
          preserveComment && commentApplicableForStepType(newType)
        )
      ).map(toLocalStep)
    );
  };

  const toggleCommentAction = (stepId: string, checked: boolean) => {
    setSteps(prev => {
      const asWorkflow = prev as unknown as WorkflowStep[];
      const synced = syncStepAllowedActionsFromPolicy(asWorkflow, stepId, checked);
      return syncAllStepsAllowedActionsFromPolicy(synced).map(toLocalStep);
    });
  };

  const toggleRequiresComment = (stepId: string, checked: boolean) => {
    setSteps(prev => {
      const withFlag = prev.map(s => (s.id === stepId ? { ...s, requiresComment: checked } : s));
      if (!checked) {
        return withFlag;
      }
      const asWorkflow = withFlag as unknown as WorkflowStep[];
      const synced = syncStepAllowedActionsFromPolicy(asWorkflow, stepId, true);
      return syncAllStepsAllowedActionsFromPolicy(synced).map(toLocalStep);
    });
  };

  const handleOpenAddAssignee = (stepId: string) => {
    setAddAssigneeStep(stepId);
    setAddAssigneeType('user');
    setAddAssigneeId('');
  };

  const handleConfirmAddAssignee = () => {
    if (!addAssigneeStep || !addAssigneeId) return;
    const isUser = addAssigneeType === 'user';
    const entity = isUser
      ? users.find(u => u.id === addAssigneeId)
      : teams.find(t => t.id === addAssigneeId);
    if (!entity) return;

    const newAssignee: LocalAssignee = {
      userId: isUser ? addAssigneeId : undefined,
      teamId: !isUser ? addAssigneeId : undefined,
      displayName: entity.name,
      type: addAssigneeType,
    };

    setSteps(prev =>
      prev.map(s =>
        s.id === addAssigneeStep
          ? { ...s, localAssignees: [...s.localAssignees, newAssignee] }
          : s
      )
    );
    setAddAssigneeStep(null);
    setAddAssigneeId('');
  };

  const handleRemoveAssignee = (stepId: string, idx: number) => {
    setSteps(prev =>
      prev.map(s => {
        if (s.id !== stepId) return s;
        const updated = s.localAssignees.filter((_, i) => i !== idx);
        return { ...s, localAssignees: updated };
      })
    );
  };

  const handleSave = () => {
    const sortedSteps = orderedSteps;
    const workflowSteps: WorkflowStep[] = sortedSteps.map(s => {
      const assignees: WorkflowStepAssignee[] = s.localAssignees.map(a => ({
        id: a.id ?? '',
        workflowStepId: s.id,
        userId: a.userId,
        teamId: a.teamId,
      }));

      const stepsSnapshot = sortedSteps as unknown as WorkflowStep[];

      return {
        id: s.id,
        workflowId: s.workflowId,
        name: s.name,
        description: s.description,
        type: s.type,
        order: s.order,
        slaId: s.slaId,
        sendEmailNotification: s.sendEmailNotification,
        requiresComment: s.requiresComment,
        approvalRule: s.approvalRule,
        minApprovals: s.minApprovals,
        allowedActions: buildAllowedActionsForStep(stepsSnapshot, s.id, stepAllowsComment(s) || !!s.requiresComment),
        assignees,
      };
    });

    const customerId = getCustomerId();
    const workflowData: Omit<Workflow, 'id'> = {
      name,
      description,
      customerId: workflow?.customerId ?? customerId,
      isSystem: workflow?.isSystem ?? false,
      isDefault: workflow?.isDefault ?? false,
      isActive: workflow?.isActive ?? true,
      steps: workflowSteps,
    };

    const defErrs = validateWorkflowDefinition(workflowSteps);
    if (defErrs.length > 0) {
      setDefinitionErrors(defErrs);
      addToast(defErrs[0] ?? 'Definição de workflow inválida.', 'error');
      return;
    }
    setDefinitionErrors([]);

    onSave(workflowData);
  };

  const toggleField = (stepId: string, field: keyof LocalStep) => (e: React.ChangeEvent<HTMLInputElement>) => {
    updateStep(stepId, { [field]: e.target.checked } as Partial<LocalStep>);
  };

  return (
    <div className="space-y-6">
      <div>
        <Input
          label="Nome do Workflow"
          value={name}
          onChange={e => setName(e.target.value)}
          required
          readOnly={readOnly}
          disabled={readOnly}
          className={readOnly ? 'bg-gray-50 text-gray-700' : ''}
        />
      </div>

      <div>
        <TextArea
          label="Descrição"
          value={description}
          onChange={e => setDescription(e.target.value)}
          readOnly={readOnly}
          disabled={readOnly}
          className={readOnly ? 'bg-gray-50 text-gray-700' : ''}
        />
      </div>

      <div className="border-t pt-4">
        {definitionErrors.length > 0 && (
          <div
            className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
            role="alert"
          >
            <p className="font-medium">A definição do fluxo não atende às regras do sistema:</p>
            <ul className="mt-2 list-disc list-inside space-y-1">
              {definitionErrors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-semibold">Etapas do Workflow</h3>
          {!readOnly && (
            <Button variant="secondary" onClick={handleAddStep} className="shrink-0 self-start sm:self-auto">
              <PlusIcon className="h-4 w-4 mr-1 inline" />
              Adicionar Etapa
            </Button>
          )}
        </div>

        {readOnly ? (
          <div className="space-y-4">
            {orderedSteps.map((step, index) => (
              <div key={step.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-sm font-medium text-gray-500 w-6 text-center">{index + 1}</span>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">{step.name || '—'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Tipo</span>
                    <p className="font-medium text-gray-800">
                      {STEP_TYPES.find(t => t.value === step.type)?.label ?? step.type}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">SLA</span>
                    <p className="font-medium text-gray-800">{slaLabel(slas, step.slaId)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Regra de aprovação</span>
                    <p className="font-medium text-gray-800">
                      {step.approvalRule === 'Any' ? 'Qualquer responsável aprova' : 'Todos os responsáveis devem aprovar'}
                    </p>
                  </div>
                  {step.approvalRule === 'Any' && (
                    <div>
                      <span className="text-gray-500">Mínimo de aprovações</span>
                      <p className="font-medium text-gray-800">{step.minApprovals}</p>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex flex-wrap gap-2 text-xs text-gray-600">
                  {[
                    { on: step.requiresComment, label: 'Requer comentário' },
                    { on: step.sendEmailNotification, label: 'E-mail na ativação' },
                  ]
                    .filter(x => x.on)
                    .map(x => (
                      <span key={x.label} className="px-2 py-0.5 rounded bg-gray-200 text-gray-800">
                        {x.label}
                      </span>
                    ))}
                </div>

                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Ações disponíveis</p>
                  {(() => {
                    const actions = displayActionsForStep(stepsForValidation, step);
                    if (actions.length === 0) {
                      return <p className="text-xs text-gray-400 italic">Nenhuma</p>;
                    }
                    return (
                      <div className="flex flex-wrap gap-2">
                        {actions.map(a => (
                          <span
                            key={a}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-800 border border-gray-300"
                          >
                            {actionLabel(a)}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Responsáveis</p>
                  {step.localAssignees.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">Nenhum responsável atribuído</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {step.localAssignees.map((assignee, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                        >
                          {assignee.type === 'user' ? (
                            <UserIcon className="h-3.5 w-3.5" />
                          ) : (
                            <UserGroupIcon className="h-3.5 w-3.5" />
                          )}
                          {assignee.displayName}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="steps">
                {(provided: DroppableProvided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-4">
                    {orderedSteps.map((step, index) => {
                      const dragLocked = !stepCanMoveSomewhere(stepsForValidation, index);
                      return (
                      <Draggable
                        key={step.id}
                        draggableId={step.id}
                        index={index}
                        isDragDisabled={dragLocked}
                      >
                        {(provided: DraggableProvided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className="bg-gray-50 border border-gray-200 rounded-lg p-4"
                          >
                          {/* Step header */}
                          <div className="flex items-center gap-2 mb-4">
                            <span
                              {...provided.dragHandleProps}
                              className={`text-gray-400 select-none px-1 ${dragLocked ? 'cursor-not-allowed opacity-50' : 'cursor-grab'}`}
                              title={
                                dragLocked
                                  ? 'Esta etapa não pode ser reordenada sem violar as regras do fluxo'
                                  : 'Arrastar para reordenar'
                              }
                            >
                              ⠿
                            </span>
                            <span className="text-sm font-medium text-gray-500 w-6 text-center">
                              {index + 1}
                            </span>
                            <div className="flex-1">
                              <input
                                type="text"
                                value={step.name}
                                onChange={e => updateStep(step.id, { name: e.target.value })}
                                placeholder="Nome da etapa"
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveStep(step.id)}
                              disabled={!canDeleteWorkflowStep(stepsForValidation, step.id)}
                              className="p-1 text-red-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                              title={
                                canDeleteWorkflowStep(stepsForValidation, step.id)
                                  ? 'Remover etapa'
                                  : 'Esta etapa não pode ser removida sem violar as regras do fluxo'
                              }
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* Tipo */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                              <select
                                value={step.type}
                                onChange={e => handleStepTypeChange(step.id, e.target.value)}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              >
                                {STEP_TYPES.map(t => (
                                  <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                              </select>
                            </div>

                            {/* SLA */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">SLA (opcional)</label>
                              <select
                                value={step.slaId ?? ''}
                                onChange={e => updateStep(step.id, { slaId: e.target.value || undefined })}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              >
                                <option value="">— Sem SLA —</option>
                                {slas.map(sla => (
                                  <option key={sla.id} value={sla.id}>{sla.name}</option>
                                ))}
                              </select>
                            </div>

                            {/* Regra de aprovação */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Regra de aprovação</label>
                              <select
                                value={step.approvalRule}
                                onChange={e => updateStep(step.id, { approvalRule: e.target.value as 'Any' | 'All' })}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              >
                                <option value="Any">Qualquer responsável aprova</option>
                                <option value="All">Todos os responsáveis devem aprovar</option>
                              </select>
                            </div>

                            {/* Mínimo de aprovações (somente quando Any) */}
                            {step.approvalRule === 'Any' && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Mínimo de aprovações</label>
                                <input
                                  type="number"
                                  min={1}
                                  value={step.minApprovals}
                                  onChange={e => updateStep(step.id, { minApprovals: Math.max(1, parseInt(e.target.value) || 1) })}
                                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                />
                              </div>
                            )}
                          </div>

                          {/* Toggles de comportamento */}
                          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-y-3 gap-x-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!step.requiresComment}
                                onChange={e => toggleRequiresComment(step.id, e.target.checked)}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">Requer comentário</span>
                            </label>

                            <label
                              className={`flex items-center gap-2 ${step.requiresComment ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                              title={step.requiresComment ? 'Obrigatório quando "Requer comentário" está marcado' : undefined}
                            >
                              <input
                                type="checkbox"
                                checked={stepAllowsComment(step) || !!step.requiresComment}
                                disabled={!!step.requiresComment}
                                onChange={e => toggleCommentAction(step.id, e.target.checked)}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
                              />
                              <span className={`text-sm ${step.requiresComment ? 'text-gray-400' : 'text-gray-700'}`}>
                                Permite comentário
                              </span>
                            </label>

                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!step.sendEmailNotification}
                                onChange={toggleField(step.id, 'sendEmailNotification')}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">Enviar e-mail na ativação</span>
                            </label>
                          </div>

                          {/* Ações disponíveis na etapa */}
                          <div className="mt-4">
                            <p className="text-sm font-medium text-gray-700 mb-1">Ações disponíveis na etapa</p>
                            <p className="text-xs text-gray-500 mb-2">
                              Definidas automaticamente conforme o tipo da etapa e a estrutura do fluxo.
                            </p>
                            {(() => {
                              const automatic = getAutomaticActionsForStep(stepsForValidation, step.id);
                              if (automatic.length === 0) {
                                return <p className="text-xs text-gray-400 italic">Nenhuma ação automática</p>;
                              }
                              return (
                                <div className="flex flex-wrap gap-2">
                                  {automatic.map(action => (
                                    <span
                                      key={action}
                                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-800 border border-gray-300"
                                    >
                                      {actionLabel(action)}
                                    </span>
                                  ))}
                                </div>
                              );
                            })()}
                          </div>

                          {/* Responsáveis da etapa */}
                          <div className="mt-4">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-sm font-medium text-gray-700">Responsáveis da etapa</p>
                              <button
                                type="button"
                                onClick={() => handleOpenAddAssignee(step.id)}
                                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                              >
                                <PlusIcon className="h-3.5 w-3.5" />
                                Adicionar
                              </button>
                            </div>

                            {step.localAssignees.length === 0 ? (
                              <p className="text-xs text-gray-400 italic">Nenhum responsável atribuído</p>
                            ) : (
                              <div className="flex flex-wrap gap-2">
                                {step.localAssignees.map((assignee, idx) => (
                                  <span
                                    key={idx}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                                  >
                                    {assignee.type === 'user' ? (
                                      <UserIcon className="h-3.5 w-3.5" />
                                    ) : (
                                      <UserGroupIcon className="h-3.5 w-3.5" />
                                    )}
                                    {assignee.displayName}
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveAssignee(step.id, idx)}
                                      className="ml-0.5 text-blue-400 hover:text-blue-700"
                                    >
                                      <XMarkIcon className="h-3 w-3" />
                                    </button>
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* Inline add assignee panel */}
                            {addAssigneeStep === step.id && (
                              <div className="mt-3 p-3 border border-gray-200 rounded-md bg-white shadow-sm">
                                <div className="flex gap-2 mb-2">
                                  <button
                                    type="button"
                                    onClick={() => { setAddAssigneeType('user'); setAddAssigneeId(''); }}
                                    className={`flex-1 py-1 text-xs rounded font-medium border transition-colors ${addAssigneeType === 'user' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'}`}
                                  >
                                    Usuário
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => { setAddAssigneeType('team'); setAddAssigneeId(''); }}
                                    className={`flex-1 py-1 text-xs rounded font-medium border transition-colors ${addAssigneeType === 'team' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'}`}
                                  >
                                    Equipe
                                  </button>
                                </div>
                                <select
                                  value={addAssigneeId}
                                  onChange={e => setAddAssigneeId(e.target.value)}
                                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm mb-2"
                                >
                                  <option value="">Selecione...</option>
                                  {addAssigneeType === 'user'
                                    ? users.map(u => (
                                        <option key={u.id} value={u.id}>{u.name} — {u.email}</option>
                                      ))
                                    : teams.map(t => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                      ))}
                                </select>
                                <div className="flex gap-2 justify-end">
                                  <button
                                    type="button"
                                    onClick={() => setAddAssigneeStep(null)}
                                    className="px-3 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                                  >
                                    Cancelar
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleConfirmAddAssignee}
                                    disabled={!addAssigneeId}
                                    className="px-3 py-1 text-xs text-white bg-blue-600 border border-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Confirmar
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                          </div>
                        )}
                      </Draggable>
                    );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </>
        )}
      </div>

      <div className="flex justify-end space-x-4 pt-4 border-t">
        <Button variant="secondary" onClick={onCancel}>
          {readOnly ? 'Fechar' : 'Cancelar'}
        </Button>
        {!readOnly && (
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!name || steps.length === 0}
          >
            Salvar
          </Button>
        )}
      </div>
    </div>
  );
};
